import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { isBroadcastOnlyPanel } from './panel-registry.js';
import { DEFAULT_GLOBAL_CURRENCY, type GlobalCurrency } from '../currency.js';

// =======================================================================
// Inter-panel data linking — the platform's primary UX pattern.
//
// Model:
//   - Tabs are top-level saved workspaces. The user has many; one is active.
//   - Each tab owns one or more LINK GROUPS. A link group has a typed
//     selection (home / operator / group / landlord) and a current
//     entity-id derived from it (the operator the selection implies).
//   - Each panel within a tab is either LINKED to a group (it reads and
//     writes that group's current entity) or PINNED (it has its own private
//     entity context, ignoring the group).
//
// Defaults:
//   - Every new tab has one default link group, id "default".
//   - Every new panel joins the default link group when added.
//   - Pinning a panel moves it from the group to its own implicit context.
//   - Unpinning rejoins the panel to the group it was last linked to (or
//     the default group if none).
//
// The "8+ linked panels stay smooth" performance contract relies on:
//   - Zustand subscribeWithSelector — panels subscribe only to the specific
//     entity-id slice they care about, not the whole store
//   - Combining a group-id change into a single event the store dispatches
//   - Panels read entity DATA from TanStack Query cache, not from this
//     store. This store carries only the ENTITY ID; the dossier comes from
//     the query cache. One fetch hydrates the cache, every linked panel
//     reads the same cached value.
//
// Persistence:
//   - localStorage at MVP via the persist middleware. Sub-unit D moves this
//     to a backend endpoint once auth (Clerk) is wired and we know the
//     org / user identity. The shape on the wire stays the same.
// =======================================================================

export type TabId = string;
export type PanelId = string;
export type LinkGroupId = string;

/** SaxoTrader-style channel colours. Each tab seeds the same four
 *  groups; panels join whichever channel matches their workflow. */
export type LinkGroupColor = 'default' | 'claret' | 'blue' | 'teal' | 'terracotta';

/** Uniform NUTS region grain (migrations 0077-0079). 'legacy' = the old
 *  per-market region field; nuts1/2/3 = the Eurostat hierarchy. */
export type RegionLevel = 'legacy' | 'nuts1' | 'nuts2' | 'nuts3';

/** What a channel selection points at (TERMINAL_HOME_GRAIN_SPEC step 2).
 *  'operator' is the legacy entity grain every pre-step-2 panel reads. */
export type SelectionKind = 'home' | 'operator' | 'group' | 'landlord';

/** A typed channel selection. `currentEntityId` is DERIVED from it (see
 *  deriveEntityId) so the entity-id panels keep working unedited. */
export interface Selection {
  kind: SelectionKind;
  /** home_key | entity_id | economic_group | landlord_key */
  key: string;
  /** Header text without a fetch. */
  label?: string;
  /** For kind 'home': the home's operator, if linked and in scope. */
  operatorEntityId?: string | null;
}

export interface LinkGroupState {
  /** Stable identifier within the tab. */
  id: LinkGroupId;
  /** Human-readable label rendered alongside grouped panels. */
  label: string;
  /** Channel colour for the panel-header indicator. */
  color: LinkGroupColor;
  /** Currently-selected entity id within the group. null = nothing selected.
   *  Derived from `selection` when one is set (see deriveEntityId). */
  currentEntityId: string | null;
  /** Typed selection (home / operator / group / landlord). Persisted v7
   *  groups LACK this field (and migrate doesn't reliably reach tabs with a
   *  restored dockview layout) — always read through normaliseSelection. */
  selection: Selection | null;
}

export interface PanelState {
  id: PanelId;
  /** Panel component key (registered in the workspace components map). */
  component: string;
  title: string;
  /**
   * Linkage state:
   *  - { kind: 'linked', groupId } → reads & writes the group's selection
   *  - { kind: 'pinned', entityId, lastGroupId } → independent of the group;
   *    remembers which group it was in so unpinning can rejoin it.
   *    `selection` is the typed twin of `entityId` (absent on legacy pins).
   */
  link:
    | { kind: 'linked'; groupId: LinkGroupId }
    | {
        kind: 'pinned';
        entityId: string | null;
        lastGroupId: LinkGroupId;
        selection?: Selection | null;
      };
  /** Local entity selection — used by broadcast-only panels (the Screener)
   *  whose own row highlight must NOT follow channel updates from other
   *  panels. The discovery context survives clicks downstream.
   *  Default undefined (legacy) → treated as null. */
  localEntityId?: string | null;
  /** Typed twin of `localEntityId` for broadcast-only panels. */
  localSelection?: Selection | null;
}

export interface TabState {
  id: TabId;
  title: string;
  /** Link groups by id. */
  groups: Record<LinkGroupId, LinkGroupState>;
  /** Panels by id. Logical state (which panels exist + what they're
   *  linked to). Physical positions / sizes / docked-vs-floating
   *  layout is in `dockviewLayout`. */
  panels: Record<PanelId, PanelState>;
  /** Opaque dockview layout snapshot — captured on every layout
   *  change so the user's arrangement survives reloads. Restored on
   *  TabCanvas mount via `api.fromJSON(...)`. Shape is
   *  `SerializedDockview` from dockview-core; kept untyped here so
   *  the store doesn't depend on the presentation library. */
  dockviewLayout?: unknown;
}

export interface AppState {
  tabs: Record<TabId, TabState>;
  /** Ordering of tabs in the tab bar. */
  tabOrder: TabId[];
  activeTabId: TabId | null;
  /** Bumped by `resetTabLayout` to force the TabCanvas to remount (it keys on
   *  this), so the tab's default panel seed runs again. Deliberately OUTSIDE
   *  `partialize` — it's a transient remount signal, not saved state. */
  layoutEpoch: Record<TabId, number>;
  /**
   * Pinned entities the user wants quick access to. Renders as a strip
   * at the top of the workspace (the SaxoTrader "favourites" / watchlist
   * concept). Order is preserved.
   */
  watchlist: string[];
  /**
   * Workspace-level filter context — one set of controls in the top bar
   * that every panel honours. Panels with their own local filter chips
   * still work, but they layer on top of this baseline.
   *
   *   activeCountry — ISO-2 code or null (= all markets)
   *   timeRange     — coarse bucket used by signal/financials views; null
   *                   means "no constraint"
   */
  filters: {
    activeCountry: string | null;
    timeRange: 'last_30d' | 'last_90d' | 'last_12m' | 'all' | null;
    /** NUTS region grain the Screener + Market Opportunity work at (migrations
     *  0077-0079). Default 'nuts2'. Read via useRegionLevel (falls back to
     *  'nuts2' for pre-existing persisted state that lacks the field). */
    regionLevel: RegionLevel;
  };
  /** Workspace-wide display currency (header selector). Controls the
   *  currency monetary values are shown in; conversions go via EUR. */
  globalCurrency: GlobalCurrency;
}

export interface AppActions {
  // --- tabs ---
  addTab: (input: { id?: TabId; title: string }) => TabId;
  removeTab: (tabId: TabId) => void;
  setActiveTab: (tabId: TabId) => void;
  setTabTitle: (tabId: TabId, title: string) => void;
  /** Drag-reorder: move `draggedId` next to `targetId` (before it, or after it
   *  when `placeAfter` — so a tab can be dropped at the very end). */
  reorderTab: (draggedId: TabId, targetId: TabId, placeAfter?: boolean) => void;
  // --- groups ---
  addGroup: (tabId: TabId, input: { id?: LinkGroupId; label: string }) => LinkGroupId;
  // --- panels ---
  addPanel: (
    tabId: TabId,
    input: { id?: PanelId; component: string; title: string; groupId?: LinkGroupId },
  ) => PanelId;
  removePanel: (tabId: TabId, panelId: PanelId) => void;
  /** Drop every panel record on `tabId` that dockview isn't actually rendering.
   *  Dockview is authoritative for what exists; the store only mirrors it. Call
   *  with the live `api.panels` ids right after a layout restore / seed. */
  pruneOrphanPanels: (tabId: TabId, liveIds: readonly PanelId[]) => void;
  // --- selection / linking ---
  /** Thin wrapper over setGroupSelection with an operator selection. */
  setGroupEntity: (tabId: TabId, groupId: LinkGroupId, entityId: string | null) => void;
  setGroupSelection: (tabId: TabId, groupId: LinkGroupId, sel: Selection | null) => void;
  /** Thin wrapper over setPanelSelection with an operator selection. */
  setPanelEntity: (tabId: TabId, panelId: PanelId, entityId: string | null) => void;
  /** Same linked / pinned / broadcast-only semantics as setPanelEntity, for
   *  any SelectionKind. currentEntityId follows via deriveEntityId. */
  setPanelSelection: (tabId: TabId, panelId: PanelId, sel: Selection | null) => void;
  pinPanel: (tabId: TabId, panelId: PanelId) => void;
  unpinPanel: (tabId: TabId, panelId: PanelId, targetGroupId?: LinkGroupId) => void;
  movePanelToGroup: (tabId: TabId, panelId: PanelId, groupId: LinkGroupId) => void;
  /** Clear the selection across every group on a tab (Esc shortcut). */
  clearSelection: (tabId: TabId) => void;
  /** Persist the dockview layout snapshot for a tab. Called debounced
   *  from the TabCanvas onDidLayoutChange handler. */
  setDockviewLayout: (tabId: TabId, layout: unknown) => void;
  /** Tidy a tab back to its default arrangement: drop every panel record and
   *  the saved geometry, then bump `layoutEpoch` so the canvas remounts and
   *  re-seeds. The only non-destructive way out of a tab that has accumulated
   *  more panels than it can legibly show. */
  resetTabLayout: (tabId: TabId) => void;
  /** Update a panel's display title (used by the inline rename UI on
   *  each tab). The dockview-side title is set via panel.api.setTitle
   *  in the same gesture. */
  setPanelTitle: (tabId: TabId, panelId: PanelId, title: string) => void;
  // --- watchlist ---
  addToWatchlist: (entityId: string) => void;
  removeFromWatchlist: (entityId: string) => void;
  // --- workspace filters ---
  setActiveCountry: (country: string | null) => void;
  setTimeRange: (range: AppState['filters']['timeRange']) => void;
  setRegionLevel: (level: RegionLevel) => void;
  setGlobalCurrency: (currency: GlobalCurrency) => void;
}

export type AppStore = AppState & { actions: AppActions };

function genId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

export const DEFAULT_GROUP_ID: LinkGroupId = 'default';

// --- selection helpers ---------------------------------------------------

/** The entity id a selection implies, given the previous one:
 *    operator         → its key
 *    home             → its operatorEntityId when non-null, else unchanged
 *    group / landlord → unchanged
 *    null             → null (clearing a selection clears the entity, as
 *                       setPanelEntity(null) always did). */
export function deriveEntityId(prev: string | null, sel: Selection | null): string | null {
  if (!sel) return null;
  if (sel.kind === 'operator') return sel.key;
  if (sel.kind === 'home') return sel.operatorEntityId ?? prev;
  return prev;
}

/** Field-wise equality (selections are small, flat records). */
export function selectionsEqual(a: Selection | null, b: Selection | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.kind === b.kind &&
    a.key === b.key &&
    a.label === b.label &&
    (a.operatorEntityId ?? null) === (b.operatorEntityId ?? null)
  );
}

// Memo for operator selections synthesised from a bare entity id, so the
// same id always yields the SAME object — selectors returning it stay
// referentially stable (a fresh object per call loops useSyncExternalStore).
const operatorSelectionMemo = new Map<string, Selection>();
function operatorSelection(entityId: string): Selection {
  let sel = operatorSelectionMemo.get(entityId);
  if (!sel) {
    if (operatorSelectionMemo.size >= 1000) operatorSelectionMemo.clear();
    sel = { kind: 'operator', key: entityId };
    operatorSelectionMemo.set(entityId, sel);
  }
  return sel;
}

/** Read-time normalisation — the load-bearing compatibility path for
 *  v7-shaped state (groups / pins / locals that carry only an entity id):
 *    selection ?? (entityId ? { kind: 'operator', key: entityId } : null)
 *  Stable: the same inputs return the same object. */
export function normaliseSelection(
  selection: Selection | null | undefined,
  entityId: string | null | undefined,
): Selection | null {
  if (selection) return selection;
  return entityId ? operatorSelection(entityId) : null;
}

/** The (selection, entityId) pair a panel reads from: its pin, its local
 *  state (broadcast-only), or its group. */
function panelSource(
  state: AppState,
  tabId: TabId,
  panelId: PanelId,
): { selection: Selection | null; entityId: string | null } | null {
  const tab = state.tabs[tabId];
  const panel = tab?.panels[panelId];
  if (!tab || !panel) return null;
  if (panel.link.kind === 'pinned') {
    return {
      selection: normaliseSelection(panel.link.selection, panel.link.entityId),
      entityId: panel.link.entityId,
    };
  }
  if (isBroadcastOnlyPanel(panel.component)) {
    const entityId = panel.localEntityId ?? null;
    return { selection: normaliseSelection(panel.localSelection, entityId), entityId };
  }
  const group = tab.groups[panel.link.groupId];
  const entityId = group?.currentEntityId ?? null;
  return { selection: normaliseSelection(group?.selection, entityId), entityId };
}

// Keep-last-accepted memory, per panel (`${tabId}\u0000${panelId}`). Lives
// outside the store: it's a read-side view concern, not persisted state, and
// writing it from a selector must not trigger a store update. Survives panel
// remounts (a ref inside the hook would not).
const lastAccepted = new Map<string, Selection>();
const panelKey = (tabId: TabId, panelId: PanelId) => `${tabId}\u0000${panelId}`;
function forgetTabSelections(tabId: TabId): void {
  const prefix = `${tabId}\u0000`;
  for (const k of lastAccepted.keys()) if (k.startsWith(prefix)) lastAccepted.delete(k);
}

/** The selection a panel that accepts `accepts` should show:
 *    1. the current selection, if its kind is accepted (and remember it);
 *    2. for operator-accepting panels whose last accepted value was an
 *       operator (or none): the operator implied by the entity id — it IS
 *       the latest operator, because a home with an operator moves it and a
 *       group / landlord leaves it alone;
 *    3. otherwise the last accepted selection (a Landlords panel doesn't go
 *       blank when someone clicks a home);
 *    4. null.
 *  Always returns a stored or memoised object — safe inside a selector. */
export function selectPanelSelection(
  state: AppState,
  tabId: TabId,
  panelId: PanelId,
  accepts: readonly SelectionKind[],
): Selection | null {
  const key = panelKey(tabId, panelId);
  const src = panelSource(state, tabId, panelId);
  if (!src) return null;
  const sel = src.selection;
  if (sel && accepts.includes(sel.kind)) {
    lastAccepted.set(key, sel);
    return sel;
  }
  const remembered = lastAccepted.get(key) ?? null;
  if (
    src.entityId &&
    accepts.includes('operator') &&
    (!remembered || remembered.kind === 'operator')
  ) {
    const op = operatorSelection(src.entityId);
    lastAccepted.set(key, op);
    return op;
  }
  if (sel === null && src.entityId === null) {
    // An explicit clear (Esc / setPanelSelection(null)) clears every panel,
    // as clearing the entity always did.
    lastAccepted.delete(key);
    return null;
  }
  return remembered;
}

const SEED_GROUPS: ReadonlyArray<{ id: LinkGroupId; label: string; color: LinkGroupColor }> = [
  { id: 'default', label: 'Default', color: 'default' },
  { id: 'claret', label: 'Claret channel', color: 'claret' },
  { id: 'blue', label: 'Blue channel', color: 'blue' },
  { id: 'teal', label: 'Teal channel', color: 'teal' },
  { id: 'terracotta', label: 'Terracotta channel', color: 'terracotta' },
];

function createDefaultTab(title: string): TabState {
  const id = genId('tab');
  const groups: Record<LinkGroupId, LinkGroupState> = {};
  for (const g of SEED_GROUPS) {
    groups[g.id] = {
      id: g.id,
      label: g.label,
      color: g.color,
      currentEntityId: null,
      selection: null,
    };
  }
  return {
    id,
    title,
    groups,
    panels: {},
  };
}

export const useAppStore = create<AppStore>()(
  devtools(
    persist(
      subscribeWithSelector((set, get) => {
        // Per interface-brief.md Section 2.1 the platform ships with
        // three default tabs: Landing (morning brief), Workspace
        // (everyday surface), Help (onboarding). Each has its own
        // panel-seed identity, attached below in shell.tsx.
        const landing = createDefaultTab('Landing');
        landing.id = 'tab_landing';
        const workspace = createDefaultTab('Workspace');
        workspace.id = 'tab_workspace';
        const help = createDefaultTab('Help');
        help.id = 'tab_help';
        return {
          tabs: {
            [landing.id]: landing,
            [workspace.id]: workspace,
            [help.id]: help,
          },
          tabOrder: [landing.id, workspace.id, help.id],
          activeTabId: workspace.id,
          layoutEpoch: {},
          watchlist: [],
          filters: { activeCountry: null, timeRange: null, regionLevel: 'nuts2' },
          globalCurrency: DEFAULT_GLOBAL_CURRENCY,
          actions: {
            addTab: (input) => {
              const tab = createDefaultTab(input.title);
              if (input.id) tab.id = input.id;
              set((s) => ({
                tabs: { ...s.tabs, [tab.id]: tab },
                tabOrder: [...s.tabOrder, tab.id],
                activeTabId: s.activeTabId ?? tab.id,
              }));
              return tab.id;
            },
            removeTab: (tabId) =>
              set((s) => {
                const { [tabId]: _removed, ...rest } = s.tabs;
                void _removed;
                forgetTabSelections(tabId);
                const nextOrder = s.tabOrder.filter((id) => id !== tabId);
                const nextActive = s.activeTabId === tabId ? (nextOrder[0] ?? null) : s.activeTabId;
                return { tabs: rest, tabOrder: nextOrder, activeTabId: nextActive };
              }),
            // Guarded: activating a tab that doesn't exist is a no-op. A
            // deleted tab's id can still reach here — the tab-strip Delete
            // menu renders in a portal, so its click bubbles through React's
            // synthetic tree to the tab's own onClick AFTER removeTab ran,
            // re-activating the dead id. A dangling activeTabId persisted and
            // rendered a permanently blank workspace (dockview mounted an
            // empty canvas for a tab with no state).
            setActiveTab: (tabId) => set((s) => (s.tabs[tabId] ? { activeTabId: tabId } : {})),

            setTabTitle: (tabId, title) =>
              set((s) => {
                const tab = s.tabs[tabId];
                if (!tab) return s;
                const trimmed = title.trim();
                if (trimmed === '' || trimmed === tab.title) return s;
                return {
                  tabs: { ...s.tabs, [tabId]: { ...tab, title: trimmed } },
                };
              }),

            reorderTab: (draggedId, targetId, placeAfter = false) =>
              set((s) => {
                if (draggedId === targetId) return s;
                const order = s.tabOrder.filter((id) => id !== draggedId);
                const idx = order.indexOf(targetId);
                if (idx === -1) return s;
                order.splice(idx + (placeAfter ? 1 : 0), 0, draggedId);
                return { tabOrder: order };
              }),

            addGroup: (tabId, input) => {
              const id = input.id ?? genId('grp');
              set((s) => {
                const tab = s.tabs[tabId];
                if (!tab) return s;
                return {
                  tabs: {
                    ...s.tabs,
                    [tabId]: {
                      ...tab,
                      groups: {
                        ...tab.groups,
                        [id]: {
                          id,
                          label: input.label,
                          color: 'default',
                          currentEntityId: null,
                          selection: null,
                        },
                      },
                    },
                  },
                };
              });
              return id;
            },

            addPanel: (tabId, input) => {
              const id = input.id ?? genId('pnl');
              const groupId = input.groupId ?? DEFAULT_GROUP_ID;
              set((s) => {
                const tab = s.tabs[tabId];
                if (!tab) return s;
                const panel: PanelState = {
                  id,
                  component: input.component,
                  title: input.title,
                  link: { kind: 'linked', groupId },
                };
                return {
                  tabs: {
                    ...s.tabs,
                    [tabId]: { ...tab, panels: { ...tab.panels, [id]: panel } },
                  },
                };
              });
              return id;
            },
            removePanel: (tabId, panelId) =>
              set((s) => {
                const tab = s.tabs[tabId];
                if (!tab) return s;
                const { [panelId]: _removed, ...restPanels } = tab.panels;
                void _removed;
                lastAccepted.delete(panelKey(tabId, panelId));
                return {
                  tabs: { ...s.tabs, [tabId]: { ...tab, panels: restPanels } },
                };
              }),
            pruneOrphanPanels: (tabId, liveIds) =>
              set((s) => {
                const tab = s.tabs[tabId];
                if (!tab) return s;
                const live = new Set<string>(liveIds);
                const kept = Object.fromEntries(
                  Object.entries(tab.panels).filter(([id]) => live.has(id)),
                );
                // Identity-stable no-op when nothing is orphaned, so the
                // `panels` selector doesn't churn on every tab mount.
                if (Object.keys(kept).length === Object.keys(tab.panels).length) return s;
                return {
                  tabs: { ...s.tabs, [tabId]: { ...tab, panels: kept } },
                };
              }),

            setGroupEntity: (tabId, groupId, entityId) =>
              get().actions.setGroupSelection(
                tabId,
                groupId,
                entityId === null ? null : { kind: 'operator', key: entityId },
              ),

            setGroupSelection: (tabId, groupId, sel) =>
              set((s) => {
                const tab = s.tabs[tabId];
                if (!tab) return s;
                const group = tab.groups[groupId];
                if (!group) return s;
                const entityId = deriveEntityId(group.currentEntityId, sel);
                // no-op for stable subscriptions (a v7 group with only
                // currentEntityId compares via its normalised selection)
                if (
                  group.currentEntityId === entityId &&
                  selectionsEqual(normaliseSelection(group.selection, group.currentEntityId), sel)
                )
                  return s;
                return {
                  tabs: {
                    ...s.tabs,
                    [tabId]: {
                      ...tab,
                      groups: {
                        ...tab.groups,
                        [groupId]: { ...group, currentEntityId: entityId, selection: sel },
                      },
                    },
                  },
                };
              }),

            setPanelEntity: (tabId, panelId, entityId) =>
              get().actions.setPanelSelection(
                tabId,
                panelId,
                entityId === null ? null : { kind: 'operator', key: entityId },
              ),

            setPanelSelection: (tabId, panelId, sel) => {
              const state = get();
              const tab = state.tabs[tabId];
              const panel = tab?.panels[panelId];
              if (!tab || !panel) return;
              const broadcastOnly = isBroadcastOnlyPanel(panel.component);
              if (panel.link.kind === 'linked') {
                // Always broadcast to the channel so downstream panels
                // (Company Overview, sibling Watchlists, etc.) update.
                state.actions.setGroupSelection(tabId, panel.link.groupId, sel);
                // Broadcast-only panels (the Screener) also keep their
                // own local highlight — that's the value they read back
                // via usePanelEntityId, NOT the group.currentEntityId
                // a downstream panel may have rewritten.
                if (broadcastOnly) {
                  set((s) => {
                    const t = s.tabs[tabId];
                    const p = t?.panels[panelId];
                    if (!t || !p) return s;
                    const prev = p.localEntityId ?? null;
                    const localEntityId = deriveEntityId(prev, sel);
                    if (
                      p.localEntityId === localEntityId &&
                      selectionsEqual(normaliseSelection(p.localSelection, prev), sel)
                    )
                      return s;
                    return {
                      tabs: {
                        ...s.tabs,
                        [tabId]: {
                          ...t,
                          panels: {
                            ...t.panels,
                            [panelId]: { ...p, localEntityId, localSelection: sel },
                          },
                        },
                      },
                    };
                  });
                }
              } else {
                set((s) => {
                  const t = s.tabs[tabId];
                  if (!t) return s;
                  const p = t.panels[panelId];
                  if (!p || p.link.kind !== 'pinned') return s;
                  const entityId = deriveEntityId(p.link.entityId, sel);
                  return {
                    tabs: {
                      ...s.tabs,
                      [tabId]: {
                        ...t,
                        panels: {
                          ...t.panels,
                          [panelId]: { ...p, link: { ...p.link, entityId, selection: sel } },
                        },
                      },
                    },
                  };
                });
              }
            },

            pinPanel: (tabId, panelId) =>
              set((s) => {
                const tab = s.tabs[tabId];
                if (!tab) return s;
                const panel = tab.panels[panelId];
                if (!panel || panel.link.kind === 'pinned') return s;
                const group = tab.groups[panel.link.groupId];
                const entityId = group?.currentEntityId ?? null;
                const selection = normaliseSelection(group?.selection, entityId);
                return {
                  tabs: {
                    ...s.tabs,
                    [tabId]: {
                      ...tab,
                      panels: {
                        ...tab.panels,
                        [panelId]: {
                          ...panel,
                          link: {
                            kind: 'pinned',
                            entityId,
                            lastGroupId: panel.link.groupId,
                            selection,
                          },
                        },
                      },
                    },
                  },
                };
              }),

            unpinPanel: (tabId, panelId, targetGroupId) =>
              set((s) => {
                const tab = s.tabs[tabId];
                if (!tab) return s;
                const panel = tab.panels[panelId];
                if (!panel || panel.link.kind !== 'pinned') return s;
                const groupId = targetGroupId ?? panel.link.lastGroupId;
                if (!tab.groups[groupId]) return s;
                return {
                  tabs: {
                    ...s.tabs,
                    [tabId]: {
                      ...tab,
                      panels: {
                        ...tab.panels,
                        [panelId]: { ...panel, link: { kind: 'linked', groupId } },
                      },
                    },
                  },
                };
              }),

            movePanelToGroup: (tabId, panelId, groupId) =>
              set((s) => {
                const tab = s.tabs[tabId];
                if (!tab || !tab.groups[groupId]) return s;
                const panel = tab.panels[panelId];
                if (!panel) return s;
                return {
                  tabs: {
                    ...s.tabs,
                    [tabId]: {
                      ...tab,
                      panels: {
                        ...tab.panels,
                        [panelId]: { ...panel, link: { kind: 'linked', groupId } },
                      },
                    },
                  },
                };
              }),

            clearSelection: (tabId) =>
              set((s) => {
                const tab = s.tabs[tabId];
                if (!tab) return s;
                const groups: Record<LinkGroupId, LinkGroupState> = {};
                let changed = false;
                for (const [gid, g] of Object.entries(tab.groups)) {
                  if (g.currentEntityId !== null || g.selection != null) changed = true;
                  groups[gid] = { ...g, currentEntityId: null, selection: null };
                }
                if (!changed) return s;
                return {
                  tabs: { ...s.tabs, [tabId]: { ...tab, groups } },
                };
              }),

            setDockviewLayout: (tabId, layout) =>
              set((s) => {
                const tab = s.tabs[tabId];
                if (!tab) return s;
                return {
                  tabs: { ...s.tabs, [tabId]: { ...tab, dockviewLayout: layout } },
                };
              }),

            resetTabLayout: (tabId) =>
              set((s) => {
                const tab = s.tabs[tabId];
                if (!tab) return s;
                const { dockviewLayout: _dropped, ...rest } = tab;
                void _dropped;
                forgetTabSelections(tabId);
                return {
                  tabs: { ...s.tabs, [tabId]: { ...rest, panels: {} } },
                  // `?? {}` because a rehydrated store predates this field.
                  layoutEpoch: {
                    ...(s.layoutEpoch ?? {}),
                    [tabId]: ((s.layoutEpoch ?? {})[tabId] ?? 0) + 1,
                  },
                };
              }),

            setPanelTitle: (tabId, panelId, title) =>
              set((s) => {
                const tab = s.tabs[tabId];
                if (!tab) return s;
                const panel = tab.panels[panelId];
                if (!panel) return s;
                if (panel.title === title) return s;
                return {
                  tabs: {
                    ...s.tabs,
                    [tabId]: {
                      ...tab,
                      panels: { ...tab.panels, [panelId]: { ...panel, title } },
                    },
                  },
                };
              }),

            addToWatchlist: (entityId) =>
              set((s) => {
                if (s.watchlist.includes(entityId)) return s;
                return { watchlist: [...s.watchlist, entityId] };
              }),
            removeFromWatchlist: (entityId) =>
              set((s) => ({ watchlist: s.watchlist.filter((id) => id !== entityId) })),
            setActiveCountry: (country) =>
              set((s) => ({ filters: { ...s.filters, activeCountry: country } })),
            setTimeRange: (range) => set((s) => ({ filters: { ...s.filters, timeRange: range } })),
            setRegionLevel: (level) =>
              set((s) => ({ filters: { ...s.filters, regionLevel: level } })),
            setGlobalCurrency: (currency) => set(() => ({ globalCurrency: currency })),
          },
        };
      }),
      {
        // v3: floating-first seed (briefly shipped in v2) is reverted
        // to docked-split arrangement that scales with the browser
        // window. Bumping the key drops anyone's stored floating
        // layout snapshot so they actually see the new docked seed
        // instead of having the old SerializedDockview restored on
        // top. Per-tab layouts customised AFTER this version persist
        // normally via api.toJSON() → setDockviewLayout.
        //
        // v2: default tabs changed from a single "Morning review"
        // to Landing / Workspace / Help; panel catalogue gained the
        // funnel-state list panels + 5 morning-brief panels.
        name: 'hc.workspace.v3',
        version: 8,
        migrate: (persisted, version) => {
          if (!persisted || typeof persisted !== 'object') return persisted;
          const p = persisted as Record<string, unknown>;

          // Pre-v2 snapshots predate the workspace filter context.
          // Inject the default-null filters block so the rest of the
          // store can rely on it being defined.
          if (version < 2 && p['filters'] === undefined) {
            p['filters'] = { activeCountry: null, timeRange: null };
          }

          // v3 → v3-r2 (in-place, no key bump): the `landing-demand`
          // and `rejected-list` panel-components were removed. Strip
          // any panel records that reference them from every tab, and
          // clear the persisted dockview layout for any tab that
          // touched them — otherwise dockview's fromJSON instantiates
          // a panel for a component-key that no longer exists in
          // panelComponents, leaving a header with an empty body.
          if (version < 3 && p['tabs'] && typeof p['tabs'] === 'object') {
            const REMOVED = new Set(['landing-demand', 'rejected-list']);
            const tabs = p['tabs'] as Record<string, Record<string, unknown>>;
            for (const tab of Object.values(tabs)) {
              const panels = tab['panels'] as
                | Record<string, { component?: string } | undefined>
                | undefined;
              if (!panels) continue;
              let dirty = false;
              for (const [panelId, panel] of Object.entries(panels)) {
                if (panel && REMOVED.has(panel.component ?? '')) {
                  delete panels[panelId];
                  dirty = true;
                }
              }
              if (dirty) delete tab['dockviewLayout'];
            }
          }

          // v6 → v7: the `landing-buyer` panel-component (M&A Activity
          // Tracker) was RETIRED — deal tracking is off-thesis. Same strip
          // as the v3 landing-demand removal: drop panel records that
          // reference it and clear the persisted dockview layout of any
          // tab that touched them. backend-sync's REMOVED_COMPONENTS
          // carries the same key so server snapshots can't resurrect it.
          if (version < 7 && p['tabs'] && typeof p['tabs'] === 'object') {
            const tabs = p['tabs'] as Record<string, Record<string, unknown>>;
            for (const tab of Object.values(tabs)) {
              const panels = tab['panels'] as
                | Record<string, { component?: string } | undefined>
                | undefined;
              if (!panels) continue;
              let dirty = false;
              for (const [panelId, panel] of Object.entries(panels)) {
                if (panel && panel.component === 'landing-buyer') {
                  delete panels[panelId];
                  dirty = true;
                }
              }
              if (dirty) delete tab['dockviewLayout'];
            }
          }

          // v3 → v4: the workspace market dropdown was removed from the
          // header, leaving `filters.activeCountry` as a ghost filter —
          // a persisted non-null value (e.g. 'NO') silently hides every
          // market-gated panel (watchlist, op-pressure, M&A) with no UI
          // to clear it. Reset to null so panels show all markets again.
          if (version < 4 && p['filters'] && typeof p['filters'] === 'object') {
            (p['filters'] as Record<string, unknown>)['activeCountry'] = null;
          }

          // v4 → v5: the link-channel colours were renamed (red→claret,
          // green→teal, yellow→terracotta) in both the LinkGroupColor enum and
          // the i18n keys. Old persisted per-tab groups still carry the retired
          // ids/colours, so the channel picker renders raw
          // "channelPicker.colors.red" labels and blank swatches. Remap each
          // tab's groups (id + colour + label) and any panel links that point
          // at them. (Guarded < 6, not < 5, because a dev build briefly shipped
          // a v5 that bumped the version without this remap.)
          if (version < 6 && p['tabs'] && typeof p['tabs'] === 'object') {
            const ID_MAP: Record<string, string> = {
              red: 'claret',
              green: 'teal',
              yellow: 'terracotta',
            };
            const LABEL_MAP: Record<string, string> = {
              red: 'Claret channel',
              green: 'Teal channel',
              yellow: 'Terracotta channel',
            };
            const tabs = p['tabs'] as Record<string, Record<string, unknown>>;
            for (const tab of Object.values(tabs)) {
              const groups = tab['groups'] as Record<string, Record<string, unknown>> | undefined;
              if (groups) {
                for (const [oldId, newId] of Object.entries(ID_MAP)) {
                  const g = groups[oldId];
                  if (!g) continue;
                  groups[newId] = {
                    ...g,
                    id: newId,
                    color: newId,
                    label: LABEL_MAP[oldId] ?? g['label'],
                  };
                  delete groups[oldId];
                }
              }
              const panels = tab['panels'] as
                | Record<string, { link?: Record<string, unknown> } | undefined>
                | undefined;
              if (panels) {
                for (const panel of Object.values(panels)) {
                  const link = panel?.link;
                  if (!link) continue;
                  for (const key of ['groupId', 'lastGroupId']) {
                    const v = link[key];
                    if (typeof v === 'string' && ID_MAP[v]) link[key] = ID_MAP[v];
                  }
                }
              }
            }
          }

          // v7 → v8: link groups carry a typed `selection` (home / operator /
          // group / landlord) alongside currentEntityId. Best-effort only —
          // migrate doesn't reliably reach tabs whose dockview layout is
          // restored, so every read ALSO goes through normaliseSelection.
          if (version < 8 && p['tabs'] && typeof p['tabs'] === 'object') {
            const tabs = p['tabs'] as Record<string, Record<string, unknown>>;
            for (const tab of Object.values(tabs)) {
              const groups = tab['groups'] as Record<string, Record<string, unknown>> | undefined;
              if (!groups) continue;
              for (const g of Object.values(groups)) {
                if (!g || g['selection'] !== undefined) continue;
                const id = g['currentEntityId'];
                g['selection'] =
                  typeof id === 'string' && id ? { kind: 'operator', key: id } : null;
              }
            }
          }

          return persisted;
        },
        partialize: (s) => ({
          tabs: s.tabs,
          tabOrder: s.tabOrder,
          activeTabId: s.activeTabId,
          watchlist: s.watchlist,
          filters: s.filters,
          globalCurrency: s.globalCurrency,
        }),
      },
    ),
    { name: 'hc-app-store' },
  ),
);

// =========================================================================
// Selectors / hooks — what panels use.
//
// Panels never read the store via `useAppStore(s => s.tabs[...].something)`
// because the whole-store subscription means every state change re-renders
// every panel. Instead they go through these narrow selectors which subscribe
// to a thin slice and only re-render when that slice changes.
// =========================================================================

/** The entity id this panel currently shows (linked or pinned). For
 *  broadcast-only panels (the Screener) we return the panel's own
 *  localEntityId rather than the group's currentEntityId, so a
 *  downstream panel's selection can't reach back and overwrite the
 *  Screener's row highlight. The Screener still broadcasts on its own
 *  clicks — see setPanelEntity. */
export function usePanelEntityId(tabId: TabId, panelId: PanelId): string | null {
  return useAppStore((s) => {
    const panel = s.tabs[tabId]?.panels[panelId];
    if (!panel) return null;
    if (panel.link.kind === 'pinned') return panel.link.entityId;
    if (isBroadcastOnlyPanel(panel.component)) return panel.localEntityId ?? null;
    return s.tabs[tabId]?.groups[panel.link.groupId]?.currentEntityId ?? null;
  });
}

/** The typed selection this panel follows, restricted to the kinds it
 *  `accepts` (see selectPanelSelection for keep-last-accepted). Pass a
 *  module-level constant or panelAccepts(component) for `accepts`; an inline
 *  literal also works (the result, not the argument, is what's compared). */
export function usePanelSelection(
  tabId: TabId,
  panelId: PanelId,
  accepts: readonly SelectionKind[],
): Selection | null {
  return useAppStore((s) => selectPanelSelection(s, tabId, panelId, accepts));
}

/** A link group's normalised selection (stable reference). */
export function useGroupSelection(tabId: TabId, groupId: LinkGroupId): Selection | null {
  return useAppStore((s) => {
    const g = s.tabs[tabId]?.groups[groupId];
    return normaliseSelection(g?.selection, g?.currentEntityId);
  });
}

/** The entity id a link group currently has selected. */
export function useGroupEntityId(tabId: TabId, groupId: LinkGroupId): string | null {
  return useAppStore((s) => s.tabs[tabId]?.groups[groupId]?.currentEntityId ?? null);
}

/** The panel record (for headers, link state, etc.). */
export function usePanel(tabId: TabId, panelId: PanelId): PanelState | undefined {
  return useAppStore((s) => s.tabs[tabId]?.panels[panelId]);
}

/** All panels for a tab in declaration order. */
// Object.values() returns a new array reference on every call, so the
// selector must be wrapped in useShallow to compare element-wise; without
// this, useSyncExternalStore enters a commit→re-render loop and bails with
// React error #185 ("Maximum update depth exceeded").
export function useTabPanels(tabId: TabId): PanelState[] {
  return useAppStore(
    useShallow((s) => {
      const tab = s.tabs[tabId];
      return tab ? Object.values(tab.panels) : [];
    }),
  );
}

/** Active tab id. */
export function useActiveTabId(): TabId | null {
  return useAppStore((s) => s.activeTabId);
}

/** Tab order — primitive string[] selector, stable when ordering unchanged. */
export function useTabOrder(): readonly TabId[] {
  return useAppStore(useShallow((s) => s.tabOrder));
}

/** Tab title for a single tab — primitive string. */
export function useTabTitle(tabId: TabId): string | undefined {
  return useAppStore((s) => s.tabs[tabId]?.title);
}

/** Bound action — call this when a panel wants to push a new selection. */
export function useSetPanelEntity(tabId: TabId, panelId: PanelId): (id: string | null) => void {
  const setPanelEntity = useAppStore((s) => s.actions.setPanelEntity);
  return (id) => setPanelEntity(tabId, panelId, id);
}

/** Bound action — push a typed selection (home / operator / group / landlord). */
export function useSetPanelSelection(
  tabId: TabId,
  panelId: PanelId,
): (sel: Selection | null) => void {
  const setPanelSelection = useAppStore((s) => s.actions.setPanelSelection);
  return (sel) => setPanelSelection(tabId, panelId, sel);
}

/** Workspace-level filters — the top-bar controls every panel honours. */
export function useWorkspaceFilters(): AppState['filters'] {
  return useAppStore(useShallow((s) => s.filters));
}
export function useActiveCountry(): string | null {
  return useAppStore((s) => s.filters.activeCountry);
}
/** The workspace NUTS region grain. Falls back to 'nuts2' for persisted state
 *  written before the field existed (no store version bump needed). */
export function useRegionLevel(): RegionLevel {
  return useAppStore((s) => s.filters.regionLevel ?? 'nuts2');
}
export function useSetRegionLevel(): (level: RegionLevel) => void {
  return useAppStore((s) => s.actions.setRegionLevel);
}

/** The workspace-wide display currency (header selector). */
export function useGlobalCurrency(): GlobalCurrency {
  return useAppStore((s) => s.globalCurrency);
}
export function useSetGlobalCurrency(): (currency: GlobalCurrency) => void {
  return useAppStore((s) => s.actions.setGlobalCurrency);
}
export function useTimeRange(): AppState['filters']['timeRange'] {
  return useAppStore((s) => s.filters.timeRange);
}
